import { Chip } from "@nt/ui/chip";
import { PageHeader } from "@nt/ui/page-header";
import { EmptyState } from "@nt/ui/states";
import { useRef, useState, type Ref } from "react";
import { useSearchParams } from "react-router";

import { CopyButton } from "@/components/copy-button";
import { CopyField } from "@/components/copy-field";
import { QueryView } from "@/components/query-view";
import { Section } from "@/components/section";
import { money, shortDate } from "@/lib/format";
import { useGet, useSession, useWindowDays, useWrite } from "@/lib/queries";
import type { LinkRow, LinksData } from "@/lib/types";
import {
  CreateLinkSheet,
  type CreateLinkInput,
} from "@/pages/links/create-link-sheet";
import { ArchiveConfirm, CustomLinksSection } from "@/pages/links/custom-links";

type Primary = NonNullable<LinksData["primary"]>;
type Notice = { tone: "success" | "danger"; text: string } | null;

function MainLinkSection({
  primary,
  windowDays,
}: {
  primary: Primary;
  windowDays: number | null;
}) {
  const within =
    windowDays === null
      ? "within your attribution window after clicking"
      : `within ${windowDays} ${windowDays === 1 ? "day" : "days"} of clicking`;
  return (
    <Section
      title="Main link"
      description={`Share it anywhere. A customer who buys ${within} counts for you.`}
      actions={
        <Chip tone="surface" className="font-mono">
          Code {primary.code}
        </Chip>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <CopyField label="Short link" value={primary.shortUrl} />
        <CopyField label="Home page link" value={primary.viaUrl} />
      </div>
    </Section>
  );
}

function DiscountCodesSection({
  campaigns,
}: {
  campaigns: LinksData["campaigns"];
}) {
  return (
    <Section
      title="Discount codes"
      description="A code gives your audience a discount and credits you even without a link click."
    >
      {campaigns.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          You have no discount codes. We set them up for you on request: write
          to support@nonresident.tax.
        </p>
      ) : (
        <ul className="grid divide-y divide-border">
          {campaigns.map((c) => {
            // The API leaves expired codes out; a page left open past the date still must not offer one.
            const expired =
              c.expiresAt !== null && Date.parse(c.expiresAt) <= Date.now();
            return (
              <li
                key={c.code}
                className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Chip tone="surface" className="font-mono">
                    {c.code}
                  </Chip>
                  <span className="text-sm text-foreground">
                    {c.discountKind === "percent"
                      ? `${c.amount}% off`
                      : `${money(c.amount)} off`}
                  </span>
                  {c.firstOrderOnly ? (
                    <Chip tone="info" size="sm">
                      First order
                    </Chip>
                  ) : null}
                  {expired ? (
                    <Chip tone="warning" size="sm">
                      Expired
                    </Chip>
                  ) : c.expiresAt ? (
                    <span className="text-sm text-muted-foreground">
                      Expires {shortDate(c.expiresAt)}
                    </span>
                  ) : null}
                </div>
                {expired ? null : (
                  <CopyButton text={c.code} label="Copy code" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/**
 * The visible copy button of a row (the table and the phone stack both
 * render one; CSS hides the other), so focus can land on the new link.
 */
function useCopyButtons() {
  const buttons = useRef(new Map<string, Set<HTMLButtonElement>>());
  const register =
    (id: string): Ref<HTMLButtonElement> =>
    (el) => {
      if (!el) return;
      const set = buttons.current.get(id) ?? new Set();
      set.add(el);
      buttons.current.set(id, set);
      return () => {
        set.delete(el);
      };
    };
  const visible = (id: string | null): HTMLButtonElement | null => {
    if (!id) return null;
    for (const el of buttons.current.get(id) ?? []) {
      if (el.getClientRects().length > 0) return el;
    }
    return null;
  };
  return { register, visible };
}

function useCreateParam(readOnly: boolean) {
  const [params, setParams] = useSearchParams();
  const open = !readOnly && params.get("create") === "1";
  const setOpen = (next: boolean) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next) out.set("create", "1");
        else out.delete("create");
        return out;
      },
      { replace: true },
    );
  return [open, setOpen] as const;
}

/** The loaded Links page: main link, custom links (with the create drawer), discount codes. */
export function LinksView({
  data,
  readOnly = false,
  windowDays = null,
}: {
  data: LinksData;
  readOnly?: boolean;
  /** The program's attribution window; null while unknown. */
  windowDays?: number | null;
}) {
  const [open, setOpen] = useCreateParam(readOnly);
  const [notice, setNotice] = useState<Notice>(null);
  const [archiving, setArchiving] = useState<LinkRow | null>(null);
  const focusAfterClose = useRef<string | null>(null);
  const copyButtons = useCopyButtons();
  const create = useWrite<LinkRow, CreateLinkInput>("/api/partner/links", [
    "links",
  ]);
  const archive = useWrite<{ archived: true }, { id: string }>(
    "/api/partner/links/archive",
    ["links"],
  );

  if (!data.primary) {
    return (
      <EmptyState
        title="You do not have a referral code yet"
        description="Your code appears here once your account is set up. If it does not, write to support@nonresident.tax."
      />
    );
  }
  const primary = data.primary;

  const openChange = (next: boolean) => {
    if (next) setNotice(null);
    else create.reset();
    setOpen(next);
  };
  const submit = (input: CreateLinkInput) =>
    create.mutate(input, {
      // useWrite refetches "links" before this runs, so the new row is on the page.
      onSuccess: (row) => {
        focusAfterClose.current = row.id;
        setNotice({
          tone: "success",
          text: `Link created: ${row.name}. Copy it below and share it.`,
        });
        openChange(false);
      },
    });
  const finalFocus = () => {
    const el = copyButtons.visible(focusAfterClose.current);
    focusAfterClose.current = null;
    return el ?? true;
  };
  const copy = (link: LinkRow) =>
    void navigator.clipboard.writeText(link.url).then(
      () =>
        setNotice({
          tone: "success",
          text: `Copied the link for ${link.name}.`,
        }),
      () =>
        setNotice({
          tone: "danger",
          text: "We could not copy the link. Select it in the list and copy it by hand.",
        }),
    );
  const startArchive = (link: LinkRow) => {
    archive.reset();
    setNotice(null);
    setArchiving(link);
  };
  const confirmArchive = (link: LinkRow) =>
    archive.mutate(
      { id: link.id },
      {
        onSuccess: () => {
          setArchiving(null);
          setNotice({ tone: "success", text: `Archived ${link.name}.` });
        },
      },
    );

  return (
    <>
      <MainLinkSection primary={primary} windowDays={windowDays} />
      <CustomLinksSection
        links={data.custom ?? []}
        readOnly={readOnly}
        onCreate={() => openChange(true)}
        onCopy={copy}
        onArchive={startArchive}
        copyRef={copyButtons.register}
        notice={notice}
        confirm={
          archiving ? (
            <ArchiveConfirm
              link={archiving}
              pending={archive.isPending}
              error={archive.error}
              onConfirm={() => confirmArchive(archiving)}
              onCancel={() => setArchiving(null)}
            />
          ) : null
        }
      />
      <DiscountCodesSection campaigns={data.campaigns} />
      <CreateLinkSheet
        open={open}
        onOpenChange={openChange}
        finalFocus={finalFocus}
        siteOrigin={data.siteOrigin}
        code={primary.code}
        pages={data.pages ?? []}
        pending={create.isPending}
        error={create.error}
        onSubmit={submit}
      />
    </>
  );
}

export function Links() {
  const query = useGet<LinksData>("links", "/api/partner/links");
  const session = useSession();
  const windowDays = useWindowDays();
  return (
    <>
      <PageHeader
        title="Links"
        description="Your main link, a custom link for each place you share, and your discount codes."
      />
      <QueryView query={query} what="your links">
        {(data) => (
          <LinksView
            data={data}
            readOnly={session.data?.state === "read_only"}
            windowDays={windowDays}
          />
        )}
      </QueryView>
    </>
  );
}
